/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { isEUCountry } from "@cosmos/common-utils";
import {
	closeRichTextHeadingGaps,
	CosmosRichText,
	CosmosTitle,
	LegalBanner,
	ResponsiveContainer,
	richTextContentStyles,
	SectionContainer,
	useScrollClientHash,
} from "@cosmos/components";
import { useAccountCountry, useGeo } from "@cosmos/next-hooks";
import { Option, Select } from "@eds/react";
import { useMessages } from "@epic-mw/i18n";
import { notFound, usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";

import { css, cx } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";

import { TemplateProviders } from "../templates";

import type { CosmosComponentContextProps } from "@cosmos/components";
import type { Eula, EulaVersion } from "@cosmos/eula-public-service";
import type { PageEulaKeyFragment } from "@cosmos/hygraph-schema";

// Helper function to get user's country code
const getUserCountryCode = (accountCountry: string, geoCountry?: string): string => {
	return accountCountry || geoCountry || "";
};

// Countries that can have their own agreement in a group, keyed by a "_<country>" suffix
const COUNTRY_AGREEMENT_CODES = new Set([
	"at",
	"au",
	"be",
	"ca",
	"de",
	"es",
	"fr",
	"gb",
	"ie",
	"it",
	"jp",
	"nl",
	"nz",
	"pt",
]);

const findAgreementBySuffix = (groupAgreements: Eula[], suffix: string): Eula | undefined =>
	groupAgreements.find(
		(agreement) => agreement.key?.toLowerCase().endsWith(`_${suffix}`) && agreement.title && agreement.body,
	);

// Helper function to find matching group agreement based on country
const findMatchingGroupAgreement = (groupAgreements: Eula[], countryCode: string): Eula | null => {
	if (!groupAgreements?.length || !countryCode) {
		return null;
	}

	// Check for a country-specific agreement
	const country = countryCode.toLowerCase();
	if (COUNTRY_AGREEMENT_CODES.has(country)) {
		const countryAgreement = findAgreementBySuffix(groupAgreements, country);
		if (countryAgreement) return countryAgreement;
	}

	// Check for EU agreement
	if (isEUCountry(countryCode)) {
		const euAgreement = findAgreementBySuffix(groupAgreements, "eu");
		if (euAgreement) return euAgreement;
	}

	return null;
};

// The key the country match wrote to the URL, per tab and path. It is a default rather than a
// choice, so a country that outranks it (the account, once a silent sign-in reloads the page)
// can still replace it. A pasted or shared link has no entry and always counts as explicit.
const autoKeyStorageKey = (pathname: string): string => `cosmos-eula-auto-key:${pathname}`;

const readAutoKey = (pathname: string): string | null => {
	try {
		return window.sessionStorage.getItem(autoKeyStorageKey(pathname));
	} catch {
		return null;
	}
};

const writeAutoKey = (pathname: string, key: string | null): void => {
	try {
		if (key) {
			window.sessionStorage.setItem(autoKeyStorageKey(pathname), key);
		} else {
			window.sessionStorage.removeItem(autoKeyStorageKey(pathname));
		}
	} catch {
		// Storage unavailable: the written key just counts as explicit, as before
	}
};

// Storage is read on every render and only changes alongside a navigation, so there is nothing to subscribe to
const subscribeToNothing = (): (() => void) => () => undefined;

const QUERY_PARAM = {
	key: "eulaKey",
	version: "eulaVersion",
	revision: "eulaRevision",
} as const;

// Both parts in one option value, so a selection round-trips through the URL
const toVersionValue = (version?: number | string | null, revision?: number | string | null): string =>
	`${version}.${revision}`;

// Effective date for a version — "(Aug 12, 2026)" in the option label
const formatVersionDate = (timestamp?: string, locale?: string): string => {
	if (!timestamp) return "";
	const date = new Date(timestamp);
	if (Number.isNaN(date.getTime())) return "";
	try {
		return new Intl.DateTimeFormat(locale || "en-US", { dateStyle: "medium", timeZone: "UTC" }).format(date);
	} catch {
		return date.toISOString().slice(0, 10);
	}
};

const eulaKeySelectRowStyles = css({
	justifyContent: "flex-end",
	paddingBottom: "eds.dimension.24",
	mdDown: {
		flexDirection: "column",
		alignItems: "center",
	},
});
const eulaKeyRegionSelectStyles = css({
	width: "340px",
	maxWidth: "full",
	mdDown: {
		marginInline: "auto",
		textAlign: "center",
		"& > div": {
			//override EDS
			alignItems: "center !important",
		},
	},
});
const eulaKeyVersionSelectStyles = css({
	width: "250px",
	maxWidth: "full",
	mdDown: {
		marginInline: "auto",
		textAlign: "center",
		"& > div": {
			//override EDS
			alignItems: "center !important",
		},
	},
});
const eulaKeyTitleStyles = css({
	textAlign: "center",
	mdDown: {
		fontSize: "28px",
	},
});
const eulaKeyBodyStyles = css({ maxWidth: "full" });

interface Props {
	pageData?: PageEulaKeyFragment;
	componentContext?: CosmosComponentContextProps;
	eula?: Eula;
	groupAgreements?: Eula[];
	versions?: EulaVersion[];
	isGameMode?: boolean;
	locale?: string;
}

export const CosmosPageEulaKey = ({
	pageData,
	componentContext,
	eula,
	groupAgreements,
	versions,
	isGameMode,
	locale,
}: Props): React.JSX.Element => {
	if (!eula?.key || !eula?.title || !eula?.body) {
		notFound();
	}

	const router = useRouter();
	const searchParams = useSearchParams();
	const pathname = usePathname();
	const messages = useMessages();
	const accountCountry = useAccountCountry();
	const { geo } = useGeo();

	const disclaimer = pageData?.disclaimer;
	const pageTitle = pageData?.title;
	const subtitle = pageData?.subtitle;

	const validGroupAgreements = groupAgreements && groupAgreements?.length > 1;
	const paramEulaKey = searchParams.get(QUERY_PARAM.key);
	const paramVersion = searchParams.get(QUERY_PARAM.version);
	const paramRevision = searchParams.get(QUERY_PARAM.revision);
	// Null on the server and during hydration, so the first client render matches the HTML
	const autoKey = useSyncExternalStore(
		subscribeToNothing,
		() => readAutoKey(pathname),
		() => null,
	);
	const hasExplicitKey = Boolean(paramEulaKey) && paramEulaKey !== autoKey;
	// A link that names an agreement or a version opens exactly that, whatever the visitor's country
	const hasUrlSelection = Boolean(hasExplicitKey || paramVersion || paramRevision);

	const paramAgreement = validGroupAgreements ? groupAgreements?.find((a) => a.key === paramEulaKey) : null;

	// Get user's country code
	const userCountryCode = validGroupAgreements ? getUserCountryCode(accountCountry, geo?.country) : null;
	// Find matching group agreement based on country
	const matchingAgreement =
		validGroupAgreements && userCountryCode && !hasUrlSelection
			? findMatchingGroupAgreement(groupAgreements, userCountryCode)
			: null;

	let activeEula = eula;

	const paramAgreementLocale = paramAgreement?.locale === "en" ? "en-US" : paramAgreement?.locale;
	if (paramAgreement?.title && paramAgreement?.body && paramAgreementLocale === locale) {
		if (activeEula.key !== paramAgreement.key) {
			activeEula = paramAgreement;
		}
	} else if (matchingAgreement && activeEula.key !== matchingAgreement.key) {
		activeEula = matchingAgreement;
	}

	const [selected, setSelected] = useState<Option<string>>({
		label: activeEula?.title || "",
		value: activeEula?.key || "",
	});

	const title = pageTitle || activeEula.title;
	const hasPageHeading = Boolean(title);
	const normalizedBody = useMemo(
		() => closeRichTextHeadingGaps(eula.body, hasPageHeading ? 2 : undefined),
		[eula.body, hasPageHeading],
	);

	// Memoize the options array for the Select component
	const selectOptions = useMemo(() => {
		if (!groupAgreements) return [];
		return groupAgreements.map((a) => {
			const aLocale = a?.locale === "en" ? "en-US" : a?.locale;
			if (a.key === activeEula.key && aLocale !== locale) {
				return { label: eula.title, value: eula.key };
			}
			return { label: a.title, value: a.key };
		});
	}, [groupAgreements, activeEula.key, activeEula.title, locale]);

	// Versions picker — newest first, labelled "v3/rev1 (Aug 12, 2026)".
	const versionOptions = useMemo(() => {
		if (!versions?.length) return [];
		return [...versions]
			.sort((a, b) => b.version - a.version || b.revision - a.revision)
			.map((v) => {
				const date = formatVersionDate(v.lastModifiedTimestamp || v.createdTimestamp, locale);
				const label = `v${v.version}/rev${v.revision}`;
				return { label: date ? `${label} (${date})` : label, value: toVersionValue(v.version, v.revision) };
			});
	}, [versions, locale]);

	const paramVersionOption = useMemo(
		() =>
			paramVersion && paramRevision
				? versionOptions.find((option) => option.value === toVersionValue(paramVersion, paramRevision))
				: undefined,
		[versionOptions, paramVersion, paramRevision],
	);

	// Derived, not stored: the URL params are the source of truth, and versions are
	// per-agreement. Local state could hold an option that is no longer in versionOptions
	// (agreement switch, or a geo-matched agreement that has not reached the URL yet).
	// Keyed to `eula`, not `activeEula`: both `versions` and the rendered body belong to the
	// key the server resolved, while activeEula can be a geo-matched variant whose own
	// version/revision would point at an unrelated entry in this agreement's history.
	const selectedVersion = useMemo(
		() =>
			paramVersionOption ||
			versionOptions.find((option) => option.value === toVersionValue(eula?.version, eula?.revision)) ||
			versionOptions[0],
		[versionOptions, paramVersionOption, eula?.version, eula?.revision],
	);

	// Single writer for the query string: the path is never touched, and an empty
	// query drops the "?" as well. `keepPosition` is for navigation the user didn't ask
	// for: it keeps the #hash and doesn't scroll, so a deep link isn't thrown away.
	const replaceQuery = useCallback(
		(mutate: (params: URLSearchParams) => void, keepPosition = false) => {
			const newParams = new URLSearchParams(searchParams);
			mutate(newParams);
			const query = newParams.toString();
			const url = query ? `${pathname}?${query}` : pathname;
			if (keepPosition) {
				router.replace(`${url}${window.location.hash}`, { scroll: false });
			} else {
				router.replace(url);
			}
		},
		[searchParams, router, pathname],
	);

	// Params that don't resolve: a key no agreement has, or a version/revision pair
	// this agreement doesn't publish.
	const staleParams = useMemo(() => {
		const stale: string[] = [];
		if (paramEulaKey && paramEulaKey !== eula.key && !groupAgreements?.some((a) => a.key === paramEulaKey)) {
			stale.push(QUERY_PARAM.key);
		}
		if ((paramVersion || paramRevision) && !paramVersionOption) {
			stale.push(QUERY_PARAM.version, QUERY_PARAM.revision);
		}
		return stale;
	}, [paramEulaKey, eula.key, groupAgreements, paramVersion, paramRevision, paramVersionOption]);

	useEffect(() => {
		if (!staleParams.length) return;
		replaceQuery((params) => staleParams.forEach((param) => params.delete(param)));
	}, [staleParams, replaceQuery]);

	const selectAgreement = useCallback(
		(option: Option<string>, keepPosition = false) => {
			setSelected(option);
			const newActive = groupAgreements?.find((a) => a.key === option.value);
			if (newActive) {
				replaceQuery((params) => {
					params.set(QUERY_PARAM.key, newActive.key);
					// The version/revision params belong to the agreement we're leaving
					params.delete(QUERY_PARAM.version);
					params.delete(QUERY_PARAM.revision);
				}, keepPosition);
			}
		},
		[groupAgreements, replaceQuery],
	);

	// The URL only shows the user's pick once its navigation commits, so a country that
	// resolves in between must not override it.
	const userPickedRef = useRef(false);
	const onAgreementChange = useCallback(
		(option: Option<string>) => {
			userPickedRef.current = true;
			writeAutoKey(pathname, null);
			selectAgreement(option);
		},
		[selectAgreement, pathname],
	);

	// The country match is only known on the client, but the body is fetched on the server for
	// the URL's key. Select the match as a user would, so the server fetches its body and the
	// title, select and body all describe the same agreement.
	const geoMatch = matchingAgreement && matchingAgreement.key !== eula.key ? matchingAgreement : null;
	useEffect(() => {
		if (!geoMatch || userPickedRef.current) return;
		writeAutoKey(pathname, geoMatch.key);
		selectAgreement({ label: geoMatch.title, value: geoMatch.key }, true);
	}, [geoMatch, selectAgreement, pathname]);

	const onVersionChange = useCallback(
		(option: Option<string>) => {
			const [version, revision] = option.value.split(".");
			replaceQuery((params) => {
				params.set(QUERY_PARAM.version, version);
				params.set(QUERY_PARAM.revision, revision);
			});
		},
		[replaceQuery],
	);

	useScrollClientHash();

	return (
		<div id="eula-key-template">
			<TemplateProviders componentContext={componentContext}>
				<LegalBanner
					banner={pageData?.banner}
					bannerCountryCodes={pageData?.bannerCountryCodes}
					isGameMode={isGameMode}
				/>
				<ResponsiveContainer>
					<SectionContainer>
						{validGroupAgreements || versionOptions.length ? (
							<HStack gap="eds.dimension.16" className={eulaKeySelectRowStyles}>
								{validGroupAgreements ? (
									<div className={cx("regional-select", eulaKeyRegionSelectStyles)}>
										<Select
											label={messages["epic.cosmos.agreements"] || "Agreements"}
											clearable={false}
											value={selected}
											options={selectOptions}
											onChange={onAgreementChange}
										></Select>
									</div>
								) : null}
								{versionOptions.length ? (
									<div className={cx("version-select", eulaKeyVersionSelectStyles)}>
										<Select
											label={messages["epic.cosmos.versions"] || "Versions"}
											clearable={false}
											value={selectedVersion}
											options={versionOptions}
											onChange={onVersionChange}
										></Select>
									</div>
								) : null}
							</HStack>
						) : null}
						<VStack gap="eds.dimension.48" alignItems={"flex-start"}>
							<VStack gap="eds.dimension.16" className={css({ margin: "0 auto" })}>
								{disclaimer && <CosmosRichText html={disclaimer} />}
								{title && (
									<CosmosTitle
										title={{ text: title, variant: "heading2XL", as: "h1" }}
										className={cx("eula-key-title", eulaKeyTitleStyles)}
									/>
								)}
								{subtitle && <CosmosRichText html={subtitle} />}
							</VStack>
							<div className={cx("eula-key-body", eulaKeyBodyStyles, richTextContentStyles)}>
								{/* Authored EULA HTML jumps h1→h3 (skips h2) and repeats the doc title as an
							    <h1>. Close the gaps (aria-level only); when the page title above renders an
							    h1, re-base the body's shallowest heading to h2 so it nests / drops the dup h1. */}
								<CosmosRichText html={normalizedBody} isGameMode={isGameMode} />
							</div>
						</VStack>
					</SectionContainer>
				</ResponsiveContainer>
			</TemplateProviders>
		</div>
	);
};
